import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ThemeToggle } from '@/components/ThemeToggle';
import {
  Sparkles,
  FileText,
  Bell,
  Settings,
  CreditCard,
  Menu,
  X,
  LogOut,
  Plus,
  BarChart3,
  Inbox,
  Users,
  Code,
  LayoutDashboard,
  Plug,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import bravaxLogo from '@/assets/bravaxforms-logo.png';

const getNavigation = (userPlan: string) => {
  const nav = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Forms', href: '/forms', icon: FileText },
    { name: 'Submissions', href: '/submissions', icon: Inbox },
    { name: 'Analytics', href: '/analytics', icon: BarChart3 },
    { name: 'CRM', href: '/crm', icon: Users },
    { name: 'Integrations', href: '/integrations', icon: Plug },
  ];

  const isPaid = ['starter', 'pro', 'business', 'enterprise'].includes(userPlan?.toLowerCase() || '');
  if (['pro', 'business', 'enterprise'].includes(userPlan?.toLowerCase() || '')) {
    nav.push({ name: 'API', href: '/api', icon: Code });
  }

  return nav;
};

const bottomNav = [
  { name: 'Billing', href: '/billing-new', icon: CreditCard },
  { name: 'Settings', href: '/settings', icon: Settings },
];

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = getNavigation(user?.plan || 'free');

  return (
    <div className="min-h-screen flex bg-background font-sans">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden animate-in fade-in duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-card/50 backdrop-blur-xl border-r border-border/40 flex flex-col transition-all duration-300 lg:translate-x-0 lg:static',
          sidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
            <img src={bravaxLogo} alt="Bravax Forms" className="h-6" />
          </div>
          <Button variant="ghost" size="icon" className="lg:hidden h-8 w-8" onClick={() => setSidebarOpen(false)}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Action Button */}
        <div className="px-4 py-4">
           <Button 
             className="w-full justify-start gap-2 h-10 rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/20 hover:scale-[1.02] transition-all"
             onClick={() => navigate('/ai-builder')}
           >
              <Sparkles className="h-4 w-4" />
              <span>Create with AI</span>
           </Button>
        </div>

        {/* Main Nav */}
        <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
          <p className="px-3 mb-2 text-[10px] font-bold tracking-widest text-muted-foreground/60 uppercase">Main Menu</p>
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <button
                key={item.name}
                onClick={() => { navigate(item.href); setSidebarOpen(false); }}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all group relative',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                )}
              >
                {isActive && <div className="absolute left-0 w-1 h-5 bg-primary rounded-r-full" />}
                <item.icon className={cn("h-4 w-4 flex-shrink-0 transition-transform group-hover:scale-110", isActive ? "text-primary" : "text-muted-foreground")} />
                {item.name}
              </button>
            );
          })}

          <div className="pt-8 space-y-1">
            <p className="px-3 mb-2 text-[10px] font-bold tracking-widest text-muted-foreground/60 uppercase">Workspace</p>
            {bottomNav.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <button
                  key={item.name}
                  onClick={() => { navigate(item.href); setSidebarOpen(false); }}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all group',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  )}
                >
                  <item.icon className={cn("h-4 w-4 flex-shrink-0 transition-transform group-hover:scale-110", isActive ? "text-primary" : "text-muted-foreground")} />
                  {item.name}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Bottom section */}
        <div className="p-4 mt-auto">
          <div className="rounded-2xl bg-muted/30 p-4 space-y-4">
             <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 border-2 border-background shadow-sm">
                  <AvatarFallback className="bg-primary text-primary-foreground font-bold">
                    {(user?.name?.charAt(0) || "U").toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-foreground truncate">{user?.name}</p>
                  <p className="text-[10px] text-muted-foreground truncate uppercase tracking-tighter">{user?.plan || 'Free'} Plan</p>
                </div>
             </div>
             <div className="flex items-center justify-between pt-2 border-t border-border/40">
                <ThemeToggle />
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive transition-colors" onClick={handleLogout}>
                   <LogOut className="h-4 w-4" />
                </Button>
             </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 relative overflow-hidden">
        {/* Background Gradients */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
        
        {/* Top Header */}
        <header className="sticky top-0 z-40 bg-background/60 backdrop-blur-md border-b border-border/40 lg:hidden">
          <div className="flex h-16 items-center justify-between px-6">
            <Button variant="ghost" size="icon" className="lg:hidden h-10 w-10" onClick={() => setSidebarOpen(true)}>
              <Menu className="h-5 w-5" />
            </Button>
            <img src={bravaxLogo} alt="Bravax Forms" className="h-6" />
            <div className="w-10" /> {/* Spacer */}
          </div>
        </header>

        {/* Main Viewport */}
        <main className="flex-1 overflow-y-auto scroll-smooth">
           <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
              {children}
           </div>
        </main>
      </div>
    </div>
  );
};
